import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../Sidebar/Sidebar';
import Admins from '../Admins/Admins';
import Orders from '../Orders/Orders';
import Drivers from '../Drivers/Drivers';
import DispatchPlanner from '../Pages/DispatchPlanner/DispatchPlanner';
import DispatchHistory from '../Pages/DispatchHistory/DispatchHistory';
import Settings from '../Settings/Settings';
import Welcome from '../Welcome/Welcome';
import Products from '../Products/Products';
import EventLogs from '../EventLogs/EventLogs';
import Reports from '../Reports/Reports';
import Banners from '../Homepage/Banners/Banners';
import ReelVideos from '../Homepage/ReelVideos/ReelVideos';
import Analytics from '../Pages/Analytics/Analytics';
import './Dashboard.css';
import { hasStoreFeature } from '../../constants/stores';
import { ordersAPI } from '../../config/orders-api';
import { authAPI } from '../../config/auth-api';

const Dashboard = ({ onLogout, adminData, setAdminData }) => {
  const [currentPage, setCurrentPage] = useState('welcome');
  const [ordersNavState, setOrdersNavState] = useState(null);
  const [reportsNavState, setReportsNavState] = useState(null);
  const [productsNavState, setProductsNavState] = useState(null);
  const [eventLogsFilter, setEventLogsFilter] = useState(null);
  const [dispatchHasUnsavedChanges, setDispatchHasUnsavedChanges] = useState(false);
  const [showUnsavedNavigationModal, setShowUnsavedNavigationModal] = useState(false);
  const [pendingPage, setPendingPage] = useState(null);
  const [refreshTriggers, setRefreshTriggers] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const pendingAudioRef = useRef(null);
  const handlePageChangeRef = useRef(null);
  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const onDispatchPage = ['dispatch', 'dispatch-planner', 'dispatch-history'].includes(currentPage);
    if (onDispatchPage && !hasStoreFeature(adminData, 'dispatch')) {
      setCurrentPage('welcome');
      navigate('/dashboard', { state: { page: 'welcome' }, replace: true });
    }
  }, [adminData, currentPage, navigate]);

  useEffect(() => {
    if (location.state?.page) {
      setCurrentPage(location.state.page);
      if (location.state.page === 'orders') {
        setOrdersNavState(location.state.productFilter
          ? { productFilter: location.state.productFilter }
          : null);
      }
      if (location.state.page === 'reports') {
        setReportsNavState(location.state.productFilter
          ? { productFilter: location.state.productFilter }
          : null);
      }
      if (location.state.page === 'products') {
        setProductsNavState(location.state.openProductId
          ? { openProductId: location.state.openProductId }
          : null);
      }
      if (location.state.page === 'event-logs') {
        setEventLogsFilter(location.state.eventLogFilter || null);
      }
    }
  }, [location.state]);

  const playAudio = useCallback((fileName, onEnded = null) => {
    const audio = new Audio(`/alerts/${fileName}`);
    if (onEnded) audio.addEventListener('ended', onEnded);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        pendingAudioRef.current = { fileName, onEnded };
      });
    }
  }, []);

  useEffect(() => {
    const handleFirstInteraction = () => {
      if (pendingAudioRef.current) {
        const { fileName, onEnded } = pendingAudioRef.current;
        pendingAudioRef.current = null;
        const audio = new Audio(`/alerts/${fileName}`);
        if (onEnded) audio.addEventListener('ended', onEnded);
        audio.play().catch((e) => console.error('Audio play failed after interaction:', e));
      }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  const handlePageChange = (page, resetFilters = false) => {
    if (currentPage === 'dispatch-planner' && dispatchHasUnsavedChanges && page !== 'dispatch-planner') {
      setPendingPage(page);
      setShowUnsavedNavigationModal(true);
      return;
    }

    if (currentPage === page) {
      setRefreshTriggers((prev) => ({
        ...prev,
        [page]: { count: (prev[page]?.count || 0) + 1, reset: resetFilters },
      }));
      return;
    }

    navigate('/dashboard', { state: { page } });
    setCurrentPage(page);
    if (page !== 'orders') {
      setOrdersNavState(null);
    }
    if (page !== 'reports') {
      setReportsNavState(null);
    }
    if (page !== 'products') {
      setProductsNavState(null);
    }
    if (page !== 'event-logs') {
      setEventLogsFilter(null);
    }
  };

  useEffect(() => {
    handlePageChangeRef.current = handlePageChange;
  });

  const handleAlerts = useCallback((data, currentAdminData) => {
    const orderCount = Number(data?.order) || 0;
    const orderAlertEnabled = currentAdminData?.webAlerts?.orderAlert !== false;

    if (orderCount > 0 && orderAlertEnabled) {
      const label = orderCount > 1 ? 'orders' : 'order';
      toast.info(`${orderCount} new ${label}`, {
        autoClose: 8000,
        onClick: () => handlePageChangeRef.current?.('orders', true),
        style: { cursor: 'pointer' },
      });

      if (currentPageRef.current === 'orders') {
        setRefreshTriggers((prev) => ({
          ...prev,
          orders: { count: (prev.orders?.count || 0) + 1, reset: false },
        }));
      }

      playAudio('order.mp3');
    }
  }, [playAudio]);

  useEffect(() => {
    const pollAlerts = async () => {
      try {
        const response = await ordersAPI.getAdminPolling();
        if (response.success && response.data) {
          const currentAdminData = authAPI.getAdminData();
          handleAlerts(response.data, currentAdminData);
        }
      } catch (error) {
        console.error('Polling failed:', error);
      }
    };

    pollAlerts();
    const pollingInterval = setInterval(pollAlerts, 30000);

    return () => clearInterval(pollingInterval);
  }, [handleAlerts]);

  const confirmLeaveDispatch = () => {
    setShowUnsavedNavigationModal(false);
    setDispatchHasUnsavedChanges(false);
    if (pendingPage) {
      navigate('/dashboard', { state: { page: pendingPage } });
      setCurrentPage(pendingPage);
      setPendingPage(null);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'admins':
        return <Admins adminData={adminData} setAdminData={setAdminData} />;
      case 'orders':
        return (
          <Orders
            adminData={adminData}
            productFilter={ordersNavState?.productFilter || null}
            onClearProductFilter={() => {
              setOrdersNavState(null);
              navigate('/dashboard', { state: { page: 'orders' }, replace: true });
            }}
            refreshTrigger={refreshTriggers.orders}
          />
        );
      case 'drivers':
        return <Drivers adminData={adminData} />;
      case 'dispatch':
      case 'dispatch-planner':
        return (
          <DispatchPlanner
            adminData={adminData}
            onUnsavedChange={setDispatchHasUnsavedChanges}
            onPageChange={handlePageChange}
          />
        );
      case 'dispatch-history':
        return <DispatchHistory onPageChange={handlePageChange} adminData={adminData} />;
      case 'products':
        return (
          <Products
            adminData={adminData}
            openProductId={productsNavState?.openProductId || null}
            onOpenProductConsumed={() => {
              setProductsNavState(null);
              navigate('/dashboard', { state: { page: 'products' }, replace: true });
            }}
          />
        );
      case 'homepage-banners':
        return <Banners adminData={adminData} />;
      case 'homepage-reels':
        return <ReelVideos adminData={adminData} />;
      case 'reports':
        return (
          <Reports
            adminData={adminData}
            productFilter={reportsNavState?.productFilter || null}
            onClearProductFilter={() => {
              setReportsNavState(null);
              navigate('/dashboard', { state: { page: 'reports' }, replace: true });
            }}
          />
        );
      case 'analytics':
        return <Analytics />;
      case 'settings':
        return <Settings adminData={adminData} setAdminData={setAdminData} />;
      case 'event-logs':
        return <EventLogs adminData={adminData} incomingFilter={eventLogsFilter} />;
      case 'welcome':
      default:
        return <Welcome adminData={adminData} />;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar
        onLogout={onLogout}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        adminData={adminData}
        setAdminData={setAdminData}
      />
      <main className="dashboard-main">
        <div className={`dashboard-content ${(currentPage === 'dispatch-planner' || currentPage === 'dispatch') ? 'dashboard-content--dispatch' : ''}`}>
          {renderPage()}
        </div>
      </main>

      {showUnsavedNavigationModal && (
        <div className="dispatch-unsaved-nav-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ marginTop: 0 }}>Unsaved route changes</h3>
            <p style={{ color: '#64748b' }}>
              You have unsaved dispatch changes. Leave without saving?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowUnsavedNavigationModal(false); setPendingPage(null); }}>
                Stay
              </button>
              <button type="button" onClick={confirmLeaveDispatch} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8 }}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
